import { QueryClient } from "@tanstack/react-query";
import { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { toast } from "sonner";

import { forceRefreshToken, logoutUser } from "@/lib/auth";

export function isNetworkError(error: unknown): boolean {
  return (
    error instanceof AxiosError &&
    !error.response &&
    error.code === "ERR_NETWORK"
  );
}

export const handleError = (error: unknown) => {
  if (isNetworkError(error)) {
    toast.error("Unable to connect. Please check your internet connection.");
    return Promise.reject(error);
  }

  if (error instanceof AxiosError) {
    const message =
      error.response?.data?.message || error.message || "An error occurred";
    toast.error(message);

    return Promise.reject(error);
  } else if (error instanceof Error) {
    toast.error(error.message);
    return Promise.reject(error);
  } else {
    toast.error("An unexpected error occurred");
    return Promise.reject(error);
  }
};

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

export const setupAxiosInterceptors = (axiosInstance: AxiosInstance) => {
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (isNetworkError(error)) {
        return Promise.reject(error);
      }

      if (error instanceof AxiosError && error.response?.status === 401) {
        const config = error.config as RetryableConfig | undefined;

        if (config && !config._retried) {
          config._retried = true;
          try {
            const token = await forceRefreshToken();
            config.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(config);
          } catch {
            logoutUser();
            return Promise.reject(error);
          }
        }

        logoutUser();
        return Promise.reject(error);
      }

      if (error instanceof AxiosError && error.response?.status === 404) {
        return Promise.reject(error);
      }

      handleError(error);
      return Promise.reject(error);
    },
  );
};

const handleQueryError = (error: unknown) => {
  if (isNetworkError(error)) {
    return;
  }
  if (error instanceof AxiosError && error.response?.status === 404) {
    return;
  }
  if (error instanceof AxiosError && error.response?.status === 401) {
    return;
  }
  handleError(error);
};

const NETWORK_ERROR_MAX_RETRIES = 3;

export const setupGlobalErrorHandling = (queryClient: QueryClient) => {
  queryClient.setDefaultOptions({
    queries: {
      retry: (failureCount, error) => {
        if (error instanceof AxiosError && error.response?.status === 401) {
          return false;
        }
        if (isNetworkError(error)) {
          return failureCount < NETWORK_ERROR_MAX_RETRIES;
        }
        return failureCount < 1;
      },
      retryDelay: (attemptIndex, error) => {
        if (isNetworkError(error)) {
          return Math.min(1000 * 2 ** attemptIndex, 8000);
        }
        return 1000;
      },
    },
  });

  queryClient.getQueryCache().config.onError = handleQueryError;
  queryClient.getMutationCache().config.onError = handleQueryError;
};
